import { useState, useEffect } from 'react'
import { Tag, Button, Grid, ActionSheet, Loading, Empty, Toast } from 'react-vant'
import GlobalNoticeBar from '../../components/GlobalNoticeBar'
import { Search, ChartTrendingO, MedalO, ServiceO, Description, Play, ArrowDown, AppsO, Arrow } from '@react-vant/icons'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { logout } from '../../store/userSlice'
import request from '../../api/request'
import '../home/style.css'

export default function StudentHome() {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { userInfo } = useSelector(state => state.user)

    const [loading, setLoading] = useState(true)
    const [children, setChildren] = useState([])
    const [selectedChild, setSelectedChild] = useState(null)
    const [latestTest, setLatestTest] = useState(null)
    const [showActionSheet, setShowActionSheet] = useState(false)
    const [timetable, setTimetable] = useState([])
    const [matches, setMatches] = useState([])
    const [periodConfig, setPeriodConfig] = useState({ morning: 4, afternoon: 4, evening: 0 })

    // 获取绑定的学生
    const fetchChildren = async () => {
        setLoading(true)
        try {
            const res = await request.get('/academic/students/m/children/')
            if (res.success && res.data) {
                setChildren(res.data)
                if (res.data.length > 0) {
                    setSelectedChild(res.data[0])
                }
            }
        } catch (e) {
            console.error('获取关联学生失败:', e)
        } finally {
            setLoading(false)
        }
    }

    // 获取选中学生的最新体测数据
    const fetchLatestTest = async (studentId) => {
        try {
            const yearRes = await request.get('/academic-year/current/')
            const currentYear = yearRes.current_academic_year
            
            const res = await request.get('/sports/tests/', {
                params: {
                    student_id: studentId,
                    academic_year: currentYear,
                    page_size: 1,
                }
            })
            if (res.items && res.items.length > 0) {
                setLatestTest(res.items[0])
            } else {
                setLatestTest(null)
            }
        } catch (e) {
            console.error('获取体测数据失败:', e)
        }
    }

    useEffect(() => {
        fetchChildren()
        fetchMatches()
    }, [])

    const fetchMatches = async () => {
        try {
            const res = await request.get('/sports/class-match/', { params: { limit: 5 } })
            setMatches(res || [])
        } catch (e) {
            console.error('获取比赛数据失败:', e)
        }
    }

    const fetchTimetable = async () => {
        try {
            const [structuresRes, timetableRes] = await Promise.all([
                request.get('/academic/scheduling/structures/'),
                request.get('/academic/scheduling/timetable/my/')
            ])
            
            if (structuresRes && structuresRes.length > 0) {
                const def = structuresRes.find(s => s.is_default) || structuresRes[0]
                setPeriodConfig({
                    morning: def.morning_periods || 4,
                    afternoon: def.afternoon_periods || 4,
                    evening: def.evening_periods || 0
                })
            }
            setTimetable(timetableRes || [])
        } catch (e) {
            console.error('获取课表失败:', e)
        }
    }

    useEffect(() => {
        if (selectedChild) {
            fetchLatestTest(selectedChild.id)
            fetchTimetable()
        }
    }, [selectedChild])

    const handleLogout = () => {
        dispatch(logout())
        navigate('/login')
    }

    const actions = children.map(child => ({
        name: child.real_name,
        subname: child.class_name || '未分配班级',
        value: child.id,
        color: selectedChild?.id === child.id ? 'var(--primary-color, #056b4f)' : undefined,
    }))

    const onSelectChild = (action) => {
        const child = children.find(c => c.id === action.value)
        setSelectedChild(child)
        setShowActionSheet(false)
    }

    if (loading && children.length === 0) {
        return (
            <div className="flex justify-center items-center h-screen" style={{ background: 'var(--canvas-bg, #f8fafc)' }}>
                <Loading type="spinner" color="var(--primary-color, #056b4f)" vertical>加载中...</Loading>
            </div>
        )
    }

    return (
        <div className="home-page" style={{ background: 'var(--canvas-bg, #f8fafc)', minHeight: '100vh', paddingBottom: 90 }}>
            {/* ========== 顶部 Hero 导航头 (App 风格青蓝渐变) ========== */}
            <div style={{
                background: 'var(--primary-gradient, linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%))',
                padding: '48px 20px 80px',
                color: '#fff',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <div style={{
                    position: 'absolute', top: -30, right: -30,
                    width: 140, height: 140, borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.08)'
                }} />

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{
                            width: 54, height: 54, borderRadius: '50%',
                            background: 'rgba(255,255,255,0.25)',
                            border: '2px solid rgba(255,255,255,0.4)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 22, fontWeight: 800, color: '#fff',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}>
                            {selectedChild?.real_name?.[0] || userInfo?.real_name?.[0] || '学'}
                        </div>
                        <div>
                            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                                同学，你好 👋
                            </div>
                            <h3 style={{ margin: '2px 0 0', fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '-0.3px' }}>
                                {selectedChild?.real_name || userInfo?.real_name || '学生'}
                            </h3>

                            {children.length > 1 && (
                                <div
                                    onClick={() => setShowActionSheet(true)}
                                    style={{
                                        marginTop: 6,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        background: 'rgba(255,255,255,0.2)',
                                        backdropFilter: 'blur(6px)',
                                        padding: '2px 10px',
                                        borderRadius: 12,
                                        fontSize: 11,
                                        border: '1px solid rgba(255,255,255,0.3)',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <span style={{ fontWeight: 600 }}>切换子女：{selectedChild?.real_name}</span>
                                    <ArrowDown style={{ marginLeft: 4, fontSize: 11 }} />
                                </div>
                            )}
                        </div>
                    </div>

                    <div onClick={handleLogout} style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, cursor: 'pointer' }}>
                        退出
                    </div>
                </div>
            </div>

            {/* ========== 内容区域 ========== */}
            <div style={{ marginTop: '-48px', padding: '0 16px', position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <GlobalNoticeBar />

                {!selectedChild ? (
                    <div style={{ padding: '40px 0', background: '#fff', borderRadius: 18 }}>
                        <Empty description="暂未绑定学生信息" />
                    </div>
                ) : (
                    <>
                        {/* 1. 学生基本信息卡片 */}
                        <div style={{
                            background: 'var(--card-bg, #ffffff)',
                            borderRadius: 16,
                            padding: '14px 18px',
                            border: '1px solid var(--border-subtle, #e2e8f0)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div>
                                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>
                                    {selectedChild.real_name}
                                </div>
                                <div style={{ fontSize: 12, color: 'var(--text-tertiary, #94a3b8)', marginTop: 2 }}>
                                    {selectedChild.class_name || '暂无班级'} · {selectedChild.gender === 'male' ? '男' : '女'}
                                </div>
                            </div>
                            <span style={{
                                background: '#f0fdf4', color: 'var(--primary-color, #056b4f)',
                                fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 8,
                                border: '1px solid #a7f3d0'
                            }}>
                                ID: {selectedChild.education_id || selectedChild.student_no || '—'}
                            </span>
                        </div>

                        {/* 2. 最新体测表现卡片 */}
                        <div style={{
                            background: 'var(--card-bg, #ffffff)',
                            borderRadius: 18,
                            padding: '18px 20px',
                            border: '1px solid var(--border-subtle, #e2e8f0)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ width: 4, height: 15, background: 'var(--primary-color, #056b4f)', borderRadius: 2 }} />
                                    <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>最新体测表现</span>
                                </div>
                                {latestTest && (
                                    <span style={{ fontSize: 11, color: 'var(--text-tertiary, #94a3b8)' }}>{latestTest.test_date}</span>
                                )}
                            </div>

                            {latestTest ? (
                                <div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
                                        <div style={{ textAlign: 'center', padding: '10px 4px', background: '#f8fafc', borderRadius: 12 }}>
                                            <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--primary-color, #056b4f)' }}>
                                                {latestTest.total_score || 0}
                                            </div>
                                            <div style={{ fontSize: 11, color: 'var(--text-tertiary, #94a3b8)', marginTop: 2 }}>标准分</div>
                                        </div>
                                        <div style={{ textAlign: 'center', padding: '10px 4px', background: '#f8fafc', borderRadius: 12 }}>
                                            <div style={{ fontSize: 20, fontWeight: 800, color: '#00b578' }}>
                                                {latestTest.composite_score || latestTest.total_score || 0}
                                            </div>
                                            <div style={{ fontSize: 11, color: 'var(--text-tertiary, #94a3b8)', marginTop: 2 }}>学年总分</div>
                                        </div>
                                        <div style={{ textAlign: 'center', padding: '10px 4px', background: '#f8fafc', borderRadius: 12 }}>
                                            <div style={{ fontSize: 20, fontWeight: 800, color: '#fa8c16' }}>
                                                {{
                                                    'A': '优秀', 'B': '良好', 'C': '及格', 'D': '不及格', 'F': '不及格',
                                                    '优秀': '优秀', '良好': '良好', '及格': '及格', '不及格': '不及格'
                                                }[latestTest.grade] || latestTest.grade || '良好'}
                                            </div>
                                            <div style={{ fontSize: 11, color: 'var(--text-tertiary, #94a3b8)', marginTop: 2 }}>评定等级</div>
                                        </div>
                                    </div>

                                    <Button
                                        block round
                                        style={{
                                            height: 40,
                                            background: '#f0fdf4', border: '1px solid #a7f3d0',
                                            color: 'var(--primary-color, #056b4f)', fontWeight: 600, fontSize: 13
                                        }}
                                        onClick={() => navigate(`/student/physical-test/detail/${latestTest.id}`)}
                                    >
                                        查看完整报告
                                    </Button>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-tertiary, #94a3b8)' }}>
                                    <div style={{ fontSize: 28, marginBottom: 4 }}>🏃</div>
                                    <div style={{ fontSize: 13, marginBottom: 10 }}>暂无本学年测试数据</div>
                                    <Button
                                        size="small" round
                                        style={{ color: 'var(--primary-color, #056b4f)', borderColor: '#a7f3d0', background: '#f0fdf4' }}
                                        onClick={() => navigate('/student/physical-test/query')}
                                    >
                                        历史成绩查询
                                    </Button>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* 3. 快捷服务网格 */}
                <div style={{
                    background: 'var(--card-bg, #ffffff)',
                    borderRadius: 18,
                    padding: '18px 16px',
                    border: '1px solid var(--border-subtle, #e2e8f0)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
                        <span style={{ width: 4, height: 15, background: 'var(--emerald-color, #00b578)', borderRadius: 2 }} />
                        <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>快捷服务</span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                        {[
                            { icon: <Search style={{ fontSize: 24 }} />, text: '成绩查询', path: '/student/physical-test/query', color: '#056b4f', bg: '#f0fdf4' },
                            { icon: <ChartTrendingO style={{ fontSize: 24 }} />, text: '成长档案', path: '/student/physical-test/portfolio', color: '#00b578', bg: '#ecfdf5' },
                            { icon: <ServiceO style={{ fontSize: 24 }} />, text: '免测申请', path: '/student/physical-test/exemption', color: '#fa8c16', bg: '#fff7ed' },
                            { icon: <AppsO style={{ fontSize: 24 }} />, text: '社团选课', path: '/student/clubs', color: '#7c3aed', bg: '#f5f3ff' },
                            { icon: <Description style={{ fontSize: 24 }} />, text: '我的社团', path: '/student/clubs/my', color: '#056b4f', bg: '#f0fdf4' },
                            { icon: <MedalO style={{ fontSize: 24 }} />, text: '班级赛事', path: '/student/sports/class-match', color: '#ff5e1e', bg: '#fff7ed' },
                        ].map((item, idx) => (
                            <div
                                key={idx}
                                onClick={() => navigate(item.path)}
                                style={{
                                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                                    padding: '10px 0', cursor: 'pointer'
                                }}
                            >
                                <div style={{
                                    width: 44, height: 44, borderRadius: 12,
                                    background: item.bg, color: item.color,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    marginBottom: 6
                                }}>
                                    {item.icon}
                                </div>
                                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary, #475569)' }}>{item.text}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 4. 我的课表 */}
                <div style={{
                    background: 'var(--card-bg, #ffffff)',
                    borderRadius: 18,
                    padding: '18px 16px',
                    border: '1px solid var(--border-subtle, #e2e8f0)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                        <span style={{ width: 4, height: 15, background: 'var(--primary-color, #056b4f)', borderRadius: 2 }} />
                        <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>我的课表</span>
                    </div>
                    <TimetableGrid timetable={timetable} config={periodConfig} />
                </div>

                {/* 5. 近期比赛 */}
                <div style={{
                    background: 'var(--card-bg, #ffffff)',
                    borderRadius: 18,
                    padding: '18px 20px',
                    border: '1px solid var(--border-subtle, #e2e8f0)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 4, height: 15, background: 'var(--athletic-color, #ff5e1e)', borderRadius: 2 }} />
                            <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>近期比赛</span>
                        </div>
                        <span
                            style={{ fontSize: 12, color: 'var(--primary-color, #056b4f)', fontWeight: 600, cursor: 'pointer' }}
                            onClick={() => navigate('/student/sports/class-match')}
                        >
                            全部赛事 <Arrow style={{ fontSize: 10 }} />
                        </span>
                    </div>

                    {matches.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {matches.slice(0, 3).map((match, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => navigate(`/student/sports/class-match/${match.id}`)}
                                    style={{ 
                                        padding: '12px 14px', borderRadius: 12,
                                        background: '#f8fafc', border: '1px solid #f1f5f9',
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <div>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>{match.title}</div>
                                        <div style={{ fontSize: 11, color: 'var(--text-tertiary, #94a3b8)', marginTop: 4 }}>
                                            📍 {match.venue || '学校操场'} · {match.match_date}
                                        </div>
                                    </div>
                                    <Tag
                                        type={match.status === 'ongoing' ? 'success' : 'primary'}
                                        style={{
                                            borderRadius: 6,
                                            background: match.status === 'ongoing' ? '#ecfdf5' : '#f0fdf4',
                                            border: 'none',
                                            color: match.status === 'ongoing' ? '#00b578' : '#056b4f',
                                            padding: '2px 8px', fontSize: 11, fontWeight: 600
                                        }}
                                    >
                                        {match.status === 'ongoing' ? '进行中' : (match.status === 'completed' ? '已结束' : '报名中')}
                                    </Tag>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-tertiary, #94a3b8)', fontSize: 12 }}>
                            暂无近期比赛
                        </div>
                    )}
                </div>
            </div>

            {/* 切换学生选择器 */}
            <ActionSheet
                visible={showActionSheet}
                actions={actions}
                onCancel={() => setShowActionSheet(false)}
                onSelect={onSelectChild}
                cancelText="取消"
                description="请选择要查看的学生"
                duration={300}
            />
        </div>
    )
}

// 迷你课表组件
const TimetableGrid = ({ timetable, config }) => {
    const days = ['一', '二', '三', '四', '五']
    const totalPeriods = (config.morning || 4) + (config.afternoon || 4) + (config.evening || 0)
    const displayPeriods = Array.from({ length: Math.min(totalPeriods, 8) }, (_, i) => i + 1)

    const grid = Array(totalPeriods).fill(null).map(() => Array(5).fill(null))
    timetable.forEach(item => {
        const d = (item.time_slot?.day_of_week || 0) - 1
        const p = (item.time_slot?.period_index || 0) - 1
        if (d >= 0 && d < 5 && p >= 0 && p < totalPeriods) grid[p][d] = item
    })

    const today = new Date().getDay()
    const currentDayIndex = today >= 1 && today <= 5 ? today - 1 : -1

    return (
        <div style={{ padding: '8px 0' }}>
            <div style={{ display: 'flex', marginBottom: 8 }}>
                <div style={{ width: 20 }} />
                {days.map((day, index) => (
                    <div key={index} style={{ 
                        flex: 1, 
                        textAlign: 'center', 
                        fontSize: 11, 
                        color: currentDayIndex === index ? 'var(--primary-color, #056b4f)' : '#94a3b8',
                        fontWeight: currentDayIndex === index ? 800 : 500,
                        background: currentDayIndex === index ? '#f0fdf4' : 'transparent',
                        borderRadius: 6,
                        padding: '2px 0'
                    }}>
                        {day}
                    </div>
                ))}
            </div>
            {displayPeriods.map((p, pIndex) => (
                <div key={pIndex} style={{ display: 'flex', height: 32, alignItems: 'center' }}>
                    <div style={{ width: 20, fontSize: 10, color: '#cbd5e1', textAlign: 'center', fontWeight: 'bold' }}>{p}</div>
                    {grid[pIndex].map((cell, dIndex) => (
                        <div key={dIndex} style={{ 
                            flex: 1, 
                            height: '90%', 
                            margin: '1px', 
                            borderRadius: 6,
                            background: cell ? (cell.subject?.name?.includes('体育') ? 'rgba(5, 107, 79, 0.12)' : 'rgba(5, 107, 79, 0.05)') : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 10,
                            color: cell?.subject?.name?.includes('体育') ? 'var(--primary-color, #056b4f)' : '#475569',
                            fontWeight: 'bold'
                        }}>
                            {cell?.subject?.name?.substring(0, 2)}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    )
}
